import { useEffect, useState, useCallback } from "react";
import api, { mediaUrl } from "@/lib/apiClient";
import { videoEmbed } from "@/lib/modules";
import { RichText } from "@/components/RichText";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { BookOpen, Eye, ClipboardList, Lightbulb, Pencil, Upload, Trash2, Video } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  study_week: { title: "Estudo da Semana", body: "" },
  vision: { title: "A Visão — Como Funciona", body: "" },
  leader_prep: { title: "Preparação do Líder", body: "" },
  weekly_tip: { title: "Dica da Semana", body: "", video_url: "", video_path: "" },
};

function VideoPlayer({ tip }) {
  const embed = videoEmbed(tip.video_url);
  if (embed) {
    return (
      <div className="mt-3 aspect-video w-full overflow-hidden rounded-lg border border-border" data-testid="tip-video-embed">
        <iframe src={embed} title="Dica da semana" className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      </div>
    );
  }
  if (tip.video_path) {
    return (
      <video controls className="mt-3 w-full rounded-lg border border-border" data-testid="tip-video-file">
        <source src={mediaUrl(tip.video_path)} />
      </video>
    );
  }
  if (tip.video_url) {
    return (
      <a href={tip.video_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary hover:underline" data-testid="tip-video-link">
        <Video className="h-4 w-4" /> Assistir vídeo da dica
      </a>
    );
  }
  return null;
}

function EditDialog({ content, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(content);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { if (open) setForm(content); }, [open, content]);

  const setSec = (sec, k, v) => setForm((f) => ({ ...f, [sec]: { ...f[sec], [k]: v } }));

  const save = async () => {
    try {
      const payload = {
        study_week: { title: form.study_week.title, body: form.study_week.body },
        vision: { title: form.vision.title, body: form.vision.body },
        leader_prep: { title: form.leader_prep.title, body: form.leader_prep.body },
        weekly_tip: {
          title: form.weekly_tip.title, body: form.weekly_tip.body,
          video_url: form.weekly_tip.video_url || "", video_path: form.weekly_tip.video_path || "",
        },
      };
      await api.put("/content/platform", payload);
      toast.success("Conteúdo atualizado!");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/content/platform/tip-video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, weekly_tip: { ...f.weekly_tip, video_path: data.video_path } }));
      toast.success("Vídeo enviado!");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro no upload"); }
    setUploading(false);
    e.target.value = "";
  };

  const removeVideo = async () => {
    try {
      await api.delete("/content/platform/tip-video");
      setForm((f) => ({ ...f, weekly_tip: { ...f.weekly_tip, video_path: "" } }));
      toast.success("Vídeo removido");
    } catch { toast.error("Erro"); }
  };

  const Section = ({ sec, hint }) => (
    <div className="rounded-lg border border-border p-3 space-y-2">
      <Input value={form[sec].title} onChange={(e) => setSec(sec, "title", e.target.value)} placeholder="Título" data-testid={`entry-${sec}-title`} />
      <Textarea value={form[sec].body} onChange={(e) => setSec(sec, "body", e.target.value)} rows={4} placeholder={hint} data-testid={`entry-${sec}-body`} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="edit-entry-button"><Pencil className="h-4 w-4" /> Editar entrada</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle>Editar conteúdo da entrada</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Dica: use Markdown — <code>**negrito**</code>, <code># Título</code>, listas com <code>-</code>.</p>
        <div className="space-y-4">
          <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Estudo da Semana</Label><Section sec="study_week" hint="Conteúdo do estudo desta semana…" /></div>
          <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">A Visão (como funciona)</Label><Section sec="vision" hint="Explique a visão e o funcionamento dos PGDs…" /></div>
          <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Preparação do Líder</Label><Section sec="leader_prep" hint="Como o líder deve se preparar…" /></div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Dica da Semana</Label>
            <div className="rounded-lg border border-border p-3 space-y-2">
              <Input value={form.weekly_tip.title} onChange={(e) => setSec("weekly_tip", "title", e.target.value)} placeholder="Título" data-testid="entry-weekly_tip-title" />
              <Textarea value={form.weekly_tip.body} onChange={(e) => setSec("weekly_tip", "body", e.target.value)} rows={3} placeholder="Dica prática para o líder…" data-testid="entry-weekly_tip-body" />
              <div>
                <Label className="text-xs">Link do vídeo (YouTube/Vimeo)</Label>
                <Input value={form.weekly_tip.video_url} onChange={(e) => setSec("weekly_tip", "video_url", e.target.value)} placeholder="https://youtu.be/…" data-testid="entry-tip-video-url" />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="inline-flex items-center gap-1.5 text-sm text-primary cursor-pointer hover:underline" data-testid="entry-tip-video-upload">
                  <Upload className="h-4 w-4" /> {uploading ? "Enviando…" : "Enviar vídeo (máx 60MB)"}
                  <input type="file" accept="video/*" className="hidden" onChange={uploadVideo} disabled={uploading} />
                </label>
                {form.weekly_tip.video_path && (
                  <Button variant="ghost" size="sm" className="gap-1.5 text-destructive" onClick={removeVideo} data-testid="entry-tip-video-remove"><Trash2 className="h-4 w-4" /> Remover vídeo</Button>
                )}
              </div>
              {form.weekly_tip.video_path && <p className="text-xs text-[hsl(var(--emerald))]">Vídeo anexado ✓</p>}
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="save-entry-button">Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Block({ icon: Icon, section, testid }) {
  if (!section?.title && !section?.body) return null;
  return (
    <Card className="p-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <span className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Icon className="h-5 w-5" /></span>
        <h3 className="font-display text-lg font-semibold">{section.title}</h3>
      </div>
      {section.body ? <RichText>{section.body}</RichText> : <p className="text-sm text-muted-foreground italic">Sem conteúdo ainda.</p>}
    </Card>
  );
}

function CompactBlock({ icon: Icon, section, testid }) {
  if (!section?.title && !section?.body) return null;
  return (
    <Card className="p-5 flex items-center justify-between gap-4" data-testid={testid}>
      <div className="flex items-center gap-3 min-w-0">
        <span className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><Icon className="h-5 w-5" /></span>
        <div className="min-w-0">
          <h3 className="font-display text-base font-semibold truncate">{section.title}</h3>
          <p className="text-xs text-muted-foreground">Toque para ler o conteúdo completo</p>
        </div>
      </div>
      <Dialog>
        <DialogTrigger asChild>
          <Button size="sm" variant="outline" className="shrink-0" data-testid={`${testid}-read`}>Ler</Button>
        </DialogTrigger>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Icon className="h-5 w-5 text-primary" /> {section.title}</DialogTitle>
            <DialogDescription>Conteúdo da entrada da semana.</DialogDescription>
          </DialogHeader>
          {section.body ? <RichText>{section.body}</RichText> : <p className="text-sm text-muted-foreground">Conteúdo em breve.</p>}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export function WeeklyEntry({ variant = "dashboard", isAdmin = false }) {
  const [content, setContent] = useState(null);

  const load = useCallback(() => {
    api.get("/content/platform").then((r) => setContent({ ...EMPTY, ...r.data })).catch(() => setContent(EMPTY));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!content) return null;
  const hasAny = content.study_week?.body || content.vision?.body || content.leader_prep?.body || content.weekly_tip?.body;
  if (variant === "home" && !hasAny) return null;

  return (
    <div className="space-y-4" data-testid={`weekly-entry-${variant}`}>
      {variant === "dashboard" && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-display text-xl font-semibold flex items-center gap-2"><BookOpen className="h-5 w-5 text-primary" /> Entrada da Semana</h2>
          {isAdmin && <EditDialog content={content} onSaved={load} />}
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        {variant === "home" ? (
          <>
            <CompactBlock icon={BookOpen} section={content.study_week} testid="entry-study-week" />
            <CompactBlock icon={Eye} section={content.vision} testid="entry-vision" />
          </>
        ) : (
          <>
            <CompactBlock icon={BookOpen} section={content.study_week} testid="entry-study-week" />
            <CompactBlock icon={Eye} section={content.vision} testid="entry-vision" />
            <Block icon={ClipboardList} section={content.leader_prep} testid="entry-leader-prep" />
            {(content.weekly_tip?.title || content.weekly_tip?.body || content.weekly_tip?.video_url || content.weekly_tip?.video_path) ? (
              <Card className="p-6" data-testid="entry-weekly-tip">
                <div className="flex items-center gap-2 mb-3">
                  <span className="h-9 w-9 rounded-lg bg-accent/15 text-accent flex items-center justify-center"><Lightbulb className="h-5 w-5" /></span>
                  <h3 className="font-display text-lg font-semibold">{content.weekly_tip.title || "Dica da Semana"}</h3>
                </div>
                {content.weekly_tip.body ? <RichText>{content.weekly_tip.body}</RichText> : null}
                <VideoPlayer tip={content.weekly_tip} />
              </Card>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
